import {
  Box,
  Card,
  CardContent,
  Switch,
  Typography,
  Skeleton,
  Stack,
  Chip,
  alpha,
  Tooltip,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import { DayliteContact } from '../../../../shared/types/dayliteModels';
import { ClientInviteRecord } from 'src/hooks/useClientNotifications';

interface ClientCardProps {
  contactId: string;
  role?: string;
  contactData: DayliteContact | null;
  isLoading: boolean;
  hasAccess: boolean;
  onToggleAccess: (contactId: string, enabled: boolean) => void;
  isUpdating: boolean;
  onSendInvite: (contactId: string) => void;
  isSendingInvite: boolean;
  inviteRecord?: ClientInviteRecord;
  hasAccount?: boolean; // undefined = still checking
  skipReminder?: boolean;
  onToggleSkipReminder: (contactId: string, skip: boolean) => void;
}

const formatDate = (ts: any): string => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function ClientCard({
  contactId,
  role,
  contactData,
  isLoading,
  hasAccess,
  onToggleAccess,
  isUpdating,
  onSendInvite,
  isSendingInvite,
  inviteRecord,
  hasAccount,
  skipReminder,
  onToggleSkipReminder,
}: ClientCardProps) {
  // Extract primary email
  const primaryEmail = contactData?.emails?.find((e) => e.is_primary)?.address 
    || contactData?.emails?.[0]?.address;

  // Extract primary phone using priority logic
  const getPrimaryPhone = () => {
    if (!contactData?.phone_numbers?.length) return null;
    const phones = contactData.phone_numbers;
    return (
      phones.find((p) => p.is_primary) ||
      phones.find((p) => p.note === 'From App') ||
      phones.find((p) => p.type === 'Main') ||
      phones[0]
    );
  };
  const primaryPhone = getPrimaryPhone();

  // Get company info
  const primaryCompany = contactData?.companies?.[0];

  // Role color mapping
  const getRoleColor = (roleStr?: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (roleStr?.toLowerCase()) {
      case 'client':
        return 'primary';
      case 'planner':
        return 'secondary';
      case 'bride':
      case 'groom':
        return 'success';
      default:
        return 'info';
    }
  };

  if (isLoading) {
    return (
      <Card
        sx={{
          height: '100%',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="rounded" width={50} height={24} />
            </Stack>
            <Skeleton variant="rounded" width={80} height={24} />
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="60%" height={20} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="50%" height={20} />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const fullName = contactData?.full_name || 
    [contactData?.first_name, contactData?.last_name].filter(Boolean).join(' ') ||
    'Unknown Contact';

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: hasAccess ? 'success.main' : 'grey.300',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          {/* Header with name and toggle */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName}
              </Typography>
              {contactData?.nickname && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  "{contactData.nickname}"
                </Typography>
              )}
            </Box>
            <Tooltip title={hasAccess ? 'Remove event access' : 'Grant event access'}>
              <Switch
                checked={hasAccess}
                onChange={(e) => onToggleAccess(contactId, e.target.checked)}
                disabled={isUpdating}
                color="success"
                size="small"
              />
            </Tooltip>
          </Stack>

          {/* Role chip */}
          {role && (
            <Box>
              <Chip
                label={role}
                size="small"
                color={getRoleColor(role)}
                sx={{
                  fontWeight: 500,
                }}
              />
            </Box>
          )}

          {/* Contact details */}
          <Stack spacing={1.5}>
            {/* Company */}
            {primaryCompany && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <BusinessIcon
                  sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {primaryCompany.title || 'Company Contact'}
                </Typography>
              </Stack>
            )}

            {/* Email */}
            {primaryEmail && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EmailIcon
                  sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                  }}
                />
                <Typography
                  variant="body2"
                  component="a"
                  href={`mailto:${primaryEmail}`}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {primaryEmail}
                </Typography>
              </Stack>
            )}

            {/* Phone */}
            {primaryPhone && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PhoneIcon
                  sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                  }}
                />
                <Typography
                  variant="body2"
                  component="a"
                  href={`tel:${primaryPhone.number}`}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {primaryPhone.number}
                  {primaryPhone.type && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5, color: 'text.secondary' }}
                    >
                      ({primaryPhone.type})
                    </Typography>
                  )}
                </Typography>
              </Stack>
            )}

            {/* Category */}
            {contactData?.category && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PersonIcon
                  sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {contactData.category}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Access indicator + Account status */}
          <Box
            sx={{
              mt: 'auto',
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                sx={{
                  color: hasAccess ? 'success.main' : 'text.disabled',
                  fontWeight: hasAccess ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {hasAccess ? '✓ Has Event Access' : 'No Event Access'}
              </Typography>
              {hasAccount === true && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'info.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                  Account Created
                </Typography>
              )}
              {inviteRecord && inviteRecord.sentCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Invited {inviteRecord.sentCount}x &middot; Last: {formatDate(inviteRecord.lastSentAt)}
                </Typography>
              )}
              {/* Skip reminder toggle */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipReminder ?? false}
                    onChange={(e) => onToggleSkipReminder(contactId, e.target.checked)}
                    disabled={isUpdating}
                    size="small"
                    icon={<NotificationsOffIcon sx={{ fontSize: 16, opacity: 0.4 }} />}
                    checkedIcon={<NotificationsOffIcon sx={{ fontSize: 16 }} />}
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <Typography variant="caption" color={skipReminder ? 'warning.main' : 'text.secondary'}>
                    Skip reminders
                  </Typography>
                }
                sx={{ m: 0, mt: 0.5 }}
              />
            </Stack>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Tooltip title={!primaryEmail ? 'No email address available' : ''}>
              <span style={{ width: '100%' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isSendingInvite ? <CircularProgress size={16} /> : <SendIcon />}
                  onClick={() => onSendInvite(contactId)}
                  disabled={isUpdating || isSendingInvite || !primaryEmail}
                  fullWidth
                >
                  {inviteRecord && inviteRecord.sentCount > 0 ? 'Resend Invite' : 'Send Invite'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
